import { LitElement, html } from 'lit';

import { navigate } from '../../lib/router/index.js';
import { api } from '../api/index.js';
import { loadState } from '../components/load-state.js';
import { slug } from '../format.js';
import { OPERATOR_RULES, PERSONA } from '../i18n/labels.js';
import { assistantSchema, validate } from '../params-form.js';
import { delegatesOf, orchestratorOf, teamErrors, teamOrder, withoutDelegate } from '../team.js';
import { formStyles } from '../../styles/shared/forms.js';
import { markdownStyles } from '../../styles/shared/markdown.js';
import { pageStyles } from '../../styles/shared/page.js';
import { currentViewer } from '../viewer.js';
import { revisionEditorPageStyles } from '../../styles/pages/revision-editor-page.js';

/**
 * The editor of an instance's configuration: its assistants, each in its own card with the exposed
 * parameters (generated from the params schema of its image version) and the three persona files.
 * Assistants are added by name, empty or (operators only) starting from a template of the same image
 * version, and removed. An assistant with delegates leads a team (plane/docs/team-proposal.md): the
 * cards follow the team, each orchestrator before its specialists. Their id is the slug of the name and never changes. One started from a
 * template came from it once the revision is applied: an update of the template then reaches it.
 *
 * The operator rules, at the head of the AGENTS.md of every assistant, are written here: the
 * customer reads them and cannot change them.
 *
 * It starts from a revision (`?from=<id>`, the desired one by default) and saves a new one:
 * revisions never change. "Save draft" stops there; "Save and apply" also makes it the desired
 * revision, and the instance page then shows the apply as the server reports it.
 */
class RevisionEditorPage extends LitElement {
  static properties = {
    name: { type: String },
    loaded: { state: true },
    params: { state: true },
    personas: { state: true },
    newName: { state: true },
    newTemplate: { state: true },
    templateOptions: { state: true },
    operatorRules: { state: true },
    errors: { state: true },
    saving: { state: true },
    saveError: { state: true },
    dirty: { state: true },
  };

  static styles = [
    pageStyles,
    formStyles,
    markdownStyles,
    revisionEditorPageStyles,
  ];

  constructor() {
    super();
    this.loaded = undefined;
    this.errors = {};
    this.saving = false;
    this.saveError = null;
    this.dirty = false;
    // loads once: an editor must not change under the user's hands
    this.loader = { value: undefined, error: null, refresh: () => this.load() };
  }

  connectedCallback() {
    super.connectedCallback();
    this.load();
  }

  updated(changed) {
    if (changed.has('name') && changed.get('name') !== undefined) {
      this.load();
    }
  }

  async load() {
    this.loader = { ...this.loader, value: undefined, error: null };
    this.requestUpdate();
    try {
      const [instance, viewer] = await Promise.all([api.instance(this.name), currentViewer()]);
      const from = new URLSearchParams(window.location.search).get('from') ?? instance.desiredRevision?.id ?? instance.revisions[0]?.id;
      const [base, image] = await Promise.all([from ? api.revision(this.name, from) : null, api.imageVersion(instance.imageVersion)]);
      this.loaded = { instance, base, schema: image.paramsSchema };
      const params = structuredClone(base?.params ?? {});
      this.params = { ...params, assistants: params.assistants ?? {} };
      this.personas = Object.fromEntries(
        Object.entries(base?.personas ?? {}).map(([id, persona]) => [id, { soul: '', agents: '', user: '', ...structuredClone(persona) }]),
      );
      // the templates are the operators': a customer user adds empty assistants
      this.templateOptions = viewer.operator ? (await api.templates()).filter((template) => template.imageVersion === instance.imageVersion) : [];
      // the template each assistant starts from, kept from a draft that is edited again
      this.templates = { ...(base?.templates ?? {}) };
      this.newName = '';
      this.newTemplate = '';
      this.operatorRules = base?.operatorRules ?? '';
      this.rulesChanged = false;
      this.errors = {};
      this.dirty = false;
      this.loader = { ...this.loader, value: this.loaded };
    } catch (error) {
      this.loader = { ...this.loader, error };
    }
    this.requestUpdate();
  }

  /** The errors of the params: those of the schema, then those of the team. */
  check(schema) {
    return { ...validate(schema, this.params), ...teamErrors(this.params) };
  }

  async save(apply) {
    this.errors = this.check(this.loaded.schema);
    if (Object.keys(this.errors).length > 0) {
      this.saveError = 'Some parameters are not valid: see the fields marked in red.';
      return;
    }
    this.saving = true;
    this.saveError = null;
    try {
      // rules left as they were are not sent: the backend keeps those of the desired revision, which an update from
      // the template may have changed since this page opened, or which differ from those of an older base
      const templates = Object.fromEntries(Object.entries(this.templates).filter(([id]) => id in this.params.assistants));
      const content = { params: this.params, personas: this.personas, ...(Object.keys(templates).length > 0 ? { templates } : {}) };
      const revision = await api.saveRevision(this.name, this.rulesChanged ? { ...content, operatorRules: this.operatorRules } : content);
      if (apply) {
        await api.apply(this.name, revision.id);
      }
      this.dirty = false;
      navigate(`/workspaces/${this.name}`);
    } catch (error) {
      this.saveError = error.message;
    } finally {
      this.saving = false;
    }
  }

  /** The id of the assistant named `name`: its slug, at most 32 characters as the schema wants. */
  newId(name) {
    return slug(name.trim(), 32);
  }

  /**
   * Adds an assistant named as typed, with the slug of the name as its id, which never changes: empty, or with the
   * params and the persona of the chosen template, to fill in. A first revision without operator rules also takes
   * the template's, as a workspace made from the template would.
   */
  async addAssistant(event) {
    event.preventDefault();
    const name = this.newName.trim();
    const id = this.newId(name);
    const pattern = this.loaded.schema.properties.assistants.propertyNames?.pattern;
    if (id === '' || (pattern && !new RegExp(pattern, 'u').test(id))) {
      this.saveError = 'The name of an assistant needs some letters or digits: its id is made of them.';
      return;
    }
    if (id in this.params.assistants) {
      this.saveError = `There is already an assistant with the id ${id}: choose another name.`;
      return;
    }
    let params = {};
    let persona = { soul: '', agents: '', user: '' };
    const templateId = this.newTemplate;
    if (templateId) {
      this.saving = true;
      try {
        const template = await api.template(templateId);
        params = structuredClone(template.params ?? {});
        persona = { ...persona, ...structuredClone(template.persona ?? {}) };
        if (!this.loaded.base && this.operatorRules.trim() === '' && template.operatorRules) {
          this.operatorRules = template.operatorRules;
          this.rulesChanged = true;
        }
      } catch (error) {
        this.saveError = error.message;
        return;
      } finally {
        this.saving = false;
      }
    }
    if (assistantSchema(this.loaded.schema)?.properties?.agentName) {
      params = { ...params, agentName: name };
    }
    this.params = { ...this.params, assistants: { ...this.params.assistants, [id]: params } };
    this.personas = { ...this.personas, [id]: persona };
    const { [id]: previous, ...templates } = this.templates;
    this.templates = templateId ? { ...templates, [id]: templateId } : templates;
    this.newName = '';
    this.newTemplate = '';
    this.saveError = null;
    this.dirty = true;
  }

  removeAssistant(id) {
    const { [id]: removed, ...assistants } = this.params.assistants;
    const { [id]: gone, ...personas } = this.personas;
    const { [id]: unlinked, ...templates } = this.templates;
    this.templates = templates;
    // nobody delegates to an assistant that is gone
    this.params = withoutDelegate({ ...this.params, assistants }, id);
    this.personas = personas;
    this.dirty = true;
  }

  /** The errors of an assistant's fields, keyed as its own form names them. */
  errorsOf(id) {
    const prefix = `assistants.${id}.`;
    return Object.fromEntries(
      Object.entries(this.errors)
        .filter(([key]) => key.startsWith(prefix))
        .map(([key, message]) => [key.slice(prefix.length), message]),
    );
  }

  renderOperatorRules() {
    return html`
      <div class="field">
        <label for="operator-rules">${OPERATOR_RULES.title}</label>
        <span class="help">${OPERATOR_RULES.description}</span>
        <plane-markdown-input
          input-id="operator-rules"
          rows="5"
          ?disabled=${this.saving}
          .value=${this.operatorRules}
          @input=${(event) => {
            this.operatorRules = event.target.value;
            this.rulesChanged = true;
            this.dirty = true;
          }}
        ></plane-markdown-input>
      </div>
    `;
  }

  renderPersonaFields(persona, idPrefix, onChange) {
    return PERSONA.map(
      (file) => html`
        <div class="field">
          <label for="${idPrefix}${file.key}"><code>${file.file}</code></label>
          <span class="help">${file.description}</span>
          <plane-markdown-input input-id="${idPrefix}${file.key}" rows="8" ?disabled=${this.saving} .value=${persona[file.key]} @input=${(event) => onChange(file.key, event.target.value)}></plane-markdown-input>
        </div>
      `,
    );
  }

  renderAssistants(schema) {
    const each = assistantSchema(schema);
    const ids = teamOrder(this.params);
    const max = schema.properties.assistants.maxProperties ?? Infinity;
    const nameOf = (id) => this.params.assistants[id]?.agentName || id;
    const problem = this.errors.assistants;
    return html`
      <section class="assistants">
        <h2>Assistants</h2>
        <p class="muted">
          Each assistant is an agent of the workspace, with its own parameters, persona and Telegram bot (set among the secrets of the workspace, once the
          revision is saved). Its id never changes. An assistant with delegates leads a team: people talk to it, and it asks its delegates, which have no bot of
          their own.
        </p>
        ${this.renderOperatorRules()}
        ${ids.map(
          (id) => html`
            <div class="assistant">
              <h3>
                <span>${this.params.assistants[id]?.agentName || id} <code>${id}</code></span>
                <button type="button" class="small danger" ?disabled=${this.saving || ids.length === 1} @click=${() => this.removeAssistant(id)}>Remove assistant</button>
              </h3>
              ${this.renderRole(id, nameOf)}
              <div>
                <plane-params-form
                  .schema=${each}
                  .assistants=${ids.filter((other) => other !== id).map((other) => ({ id: other, name: nameOf(other) }))}
                  .value=${this.params.assistants[id]}
                  .errors=${this.errorsOf(id)}
                  ?disabled=${this.saving}
                  @params-change=${(event) => {
                    // set as is: an assistant whose fields are all empty is still an assistant
                    this.params = { ...this.params, assistants: { ...this.params.assistants, [id]: event.detail.value } };
                    this.dirty = true;
                    if (Object.keys(this.errors).length > 0) {
                      this.errors = this.check(schema);
                    }
                  }}
                ></plane-params-form>
              </div>
              <div>
                ${this.renderPersonaFields(this.personas[id], `persona-${id}-`, (key, value) => {
                  this.personas = { ...this.personas, [id]: { ...this.personas[id], [key]: value } };
                  this.dirty = true;
                })}
              </div>
            </div>
          `,
        )}
        ${problem ? html`<p class="form-error" role="alert">${problem}</p>` : null}
        ${ids.length < max
          ? html`
              <div class="add ${this.templateOptions.length > 0 ? '' : 'no-template'}">
                <div class="field">
                  <label for="new-assistant">New assistant</label>
                  <input
                    id="new-assistant"
                    aria-describedby="new-assistant-help"
                    type="text"
                    maxlength="100"
                    ?disabled=${this.saving}
                    .value=${this.newName}
                    @input=${(event) => (this.newName = event.target.value)}
                    @keydown=${(event) => event.key === 'Enter' && this.addAssistant(event)}
                  />
                </div>
                ${this.templateOptions.length > 0
                  ? html`<div class="field">
                      <label for="new-assistant-template">Start from</label>
                      <select id="new-assistant-template" ?disabled=${this.saving} .value=${this.newTemplate} @change=${(event) => (this.newTemplate = event.target.value)}>
                        <option value="">Nothing: an empty assistant</option>
                        ${this.templateOptions.map((template) => html`<option value=${template.id} ?selected=${template.id === this.newTemplate}>${template.name}</option>`)}
                      </select>
                    </div>`
                  : null}
                <button type="button" ?disabled=${this.saving || this.newId(this.newName) === ''} @click=${(event) => this.addAssistant(event)}>Add assistant</button>
                <p class="help" id="new-assistant-help">
                  Its name, for example <em>Lavoro</em>.
                  ${this.newId(this.newName) ? html`Its id, which never changes: <code>${this.newId(this.newName)}</code>.` : 'Its id is made of the name and never changes.'}
                </p>
              </div>
            `
          : html`<p class="muted">A workspace holds at most ${max} assistants.</p>`}
      </section>
    `;
  }

  /** Where an assistant stands in the team of the workspace; nothing outside a team. */
  renderRole(id, nameOf) {
    const delegates = delegatesOf(this.params, id);
    if (delegates.length > 0) {
      return html`<p class="role">Leads a team: asks ${delegates.map((delegate, i) => html`${i > 0 ? ', ' : ''}<strong>${nameOf(delegate)}</strong>`)}.</p>`;
    }
    const orchestrator = orchestratorOf(this.params, id);
    return orchestrator
      ? html`<p class="role">In the team of <strong>${nameOf(orchestrator)}</strong>: people reach it only through ${nameOf(orchestrator)}, so it has no Telegram bot.</p>`
      : null;
  }

  render() {
    const crumbs = html`<nav class="crumbs" aria-label="Breadcrumb">
      <a href="/workspaces">Workspaces</a> / <a href="/workspaces/${this.name}">${this.loaded?.instance.displayName ?? this.name}</a> / edit
    </nav>`;
    const pending = loadState(this.loader, 'the configuration');
    if (pending) {
      return html`${crumbs}${pending}`;
    }
    const { instance, base, schema } = this.loaded;
    return html`
      ${crumbs}
      <header class="page-head">
        <div>
          <h1>Edit ${instance.displayName}</h1>
          <p class="lead">
            ${base ? html`Starting from revision ${base.number}.` : 'First revision.'} Saving creates revision
            ${Math.max(0, ...instance.revisions.map((revision) => revision.number)) + 1}: nothing changes on the server until it is applied.
          </p>
        </div>
      </header>
      <form
        @submit=${(event) => {
          event.preventDefault();
          this.save(true);
        }}
      >
        ${this.renderAssistants(schema)}
        <div class="buttons">
          <button class="primary" type="submit" ?disabled=${this.saving}>Save and apply</button>
          <button type="button" ?disabled=${this.saving} @click=${() => this.save(false)}>Save draft</button>
          <a class="button" href="/workspaces/${this.name}">Cancel</a>
          ${this.saveError
            ? html`<span class="form-error" role="alert">${this.saveError}</span>`
            : html`<span class="hint">${this.dirty ? 'Unsaved changes' : 'No changes yet'}</span>`}
        </div>
      </form>
    `;
  }
}

customElements.define('plane-revision-editor-page', RevisionEditorPage);
